/* global chrome */

import {
  LOGIN_URL,
  FALLBACK_MAX_SECONDS_PER_MOTE,
  DEFAULT_ANONYMOUS_USER_ID,
  DEFAULT_ANONYMOUS_CREATED_AT,
  ANONYMOUS_PREFIX,
  __TEST__
} from '@mote/helpers/constants'
import {
  noLog as log,
  openWindow,
  EpochToPrettyDateHoursMinutes
} from '../shared/Utils'

import { ALL_AUTH_PARAMS, MANDATORY_AUTH_PARAMS } from '@mote/helpers/constants'
import { makeAccessTokenMagic } from './testToken'
import { DiagnosticsLogger } from '@mote/helpers/diagnostics'

const logD = DiagnosticsLogger('auth2', 'Auth2')

const loggableToken = token => {
  let output
  if (!token) {
    output = token
  } else {
    output = token.substring(0, 10) + '...'
  }
  return `[${output}]`
}

class Auth2 {
  ///////////////// public mutating methods ////////////////

  constructor() {
    log('Creating new Auth object')
    this._storageProvider = __TEST__
      ? new Auth2MemoryStorage()
      : new Auth2LocalChromeStorage()
    this._callbacks = []
    this._cache = {}
    this._resetCache()
    this._connectToStorageProvider()
  }

  login(o) {
    if (!o) {
      throw new Auth2Error('login needs a populated object')
    }
    const missingKey = this._missingMinimalPopulation(o)
    if (missingKey) {
      throw new Auth2Error('login missing param on login: ' + missingKey)
    }

    logD.info('logged in with userId [' + this._cache.userId + ']')
    this.updateCacheAndStorage(o)

    makeAccessTokenMagic()
  }

  logout() {
    logD.info('Logged out')
    this._resetCache()
    this._writeAndBroadcast()
  }

  updateAuthData(data) {
    if (!this.isLoggedIn() || this._missingMinimalPopulation(this._cache)) {
      throw new Auth2Error('You must be logged in to update access token')
    }
    const token = data.accessToken
    if (!token) {
      throw new Auth2Error('You must supply a token to update')
    }
    if (token !== this._cache.accessToken) {
      logD.info(
        `Legacy: Updating accessToken from ${loggableToken(
          this._cache.accessToken
        )} to ${loggableToken(token)}`
      )
    }
    this._cache.accessToken = token
    this._cache.lastTranslationLanguage = data.lastTranslationLanguage
    this._writeAndBroadcast()
  }

  updateCacheAndStorageFromResponse(data, url) {
    const missingKey = this._missingMinimalPopulation(data)
    if (missingKey) {
      logD.error(
        `Missing required key from response: ${missingKey}: url: ${url}`,
        {
          response: JSON.stringify(data)
        }
      )
      return
    }
    this.updateCacheAndStorage(data)
  }

  updateCacheAndStorage(data) {
    for (const key in ALL_AUTH_PARAMS) {
      if (data[key] === undefined || data[key] === null) continue
      if (key === 'userId') this._cache.lastUserId = data[key]
      this._cache[key] = data[key]
    }
    if (data.accessToken !== this._cache.accessToken) {
      logD.info(
        `updateCacheAndStorage: Updating accessToken from ${loggableToken(
          this._cache.accessToken
        )} to ${loggableToken(data.accessToken)}`
      )
    }
    this._writeAndBroadcast()
  }

  updateAccessToken(token) {
    if (!this.isLoggedIn() || this._missingMinimalPopulation(this._cache)) {
      throw new Auth2Error('You must be logged in to update access token')
    }
    if (!token) {
      throw new Auth2Error('You must supply a token to update')
    }

    if (token !== this._cache.accessToken) {
      logD.info(
        `updateAccessToken: Updating from ${loggableToken(
          this._cache.accessToken
        )} to ${loggableToken(token)}`
      )
    }
    this._cache.accessToken = token
    this._writeAndBroadcast()
  }

  updateFromResponse(responseData) {
    logD.info('updateFromResponse: Got responseData:', { responseData })
    if (!this.isLoggedIn() || this._missingMinimalPopulation(this._cache)) {
      log('Skipping (no log in or initial cache)')
      return
    }
    if (!('accessToken' in responseData)) {
      logD.info('Skipping (no accessToken)')
      return
    }
    logD.info('updating cache from response', { responseData })
    if (responseData.accessToken !== this._cache.accessToken) {
      logD.info(
        `updateFromResponse. Access token change from ${loggableToken(
          this._cache.accessToken
        )} to ${loggableToken(responseData.accessToken)}`
      )
    }
    for (const key of Object.keys(responseData)) {
      if (Object.keys(ALL_AUTH_PARAMS).includes(key)) {
        this._cache[key] = responseData[key]
      }
    }
    this._writeAndBroadcast()
  }

  addListener(callback) {
    this._callbacks.push(callback)
    callback(this.isLoggedIn())
  }

  removeListener(callback) {
    this._callbacks = this._callbacks.filter(cb => callback !== cb)
  }

  sendOpenLoginWindowMessage() {
    openWindow(LOGIN_URL)
  }

  updateLastTranslationLanguage(tl) {
    this._cache.lastTranslationLanguage = tl
    this._writeAndBroadcast()
  }

  ///////////////// public accessors /////////////////////

  getAccessToken() {
    if (!this.isLoggedIn()) {
      return ''
    } else {
      return this._cache.accessToken
    }
  }

  getLastTranslationLanguage() {
    if (!this.isLoggedIn() || !this._cache.lastTranslationLanguage) {
      return ''
    } else {
      return this._cache.lastTranslationLanguage
    }
  }

  getDeviceIdOrAccessTokenHash() {
    if (!this.isLoggedIn()) {
      return { deviceId: this.getUserId() }
    } else {
      return { accessToken: this.getAccessToken() }
    }
  }

  getEmail() {
    if (!this.isLoggedIn()) {
      return ''
    } else {
      return this._cache.primaryEmailAddress
    }
  }

  getUserId() {
    if (!(this._cache && this._cache?.userId)) {
      return this._cache.anonymousUserId ?? DEFAULT_ANONYMOUS_USER_ID
    } else {
      return this._cache.userId
    }
  }

  getLastUserId() {
    return this._cache.lastUserId
  }

  getName() {
    if (!(this._cache && this._cache.name)) {
      return 'Someone'
    } else {
      return this._cache.name
    }
  }

  getCreatedByGreeting() {
    if (!(this._cache && this._cache.createdByGreeting)) {
      return 'Someone'
    } else {
      return this._cache.createdByGreeting
    }
  }

  getCreatedAt() {
    if (!(this._cache && this._cache.createdAt)) {
      return Math.floor(Date.now() / 1000)
    } else {
      return this._cache.createdAt
    }
  }

  getQuotaResetsAt() {
    if (!(this._cache && this._cache.quotaResetsAt)) {
      return Math.floor(Date.now() / 1000)
    } else {
      return parseInt(this._cache.quotaResetsAt)
    }
  }

  getPreferredLocale() {
    if (!this.isLoggedIn()) {
      return ''
    } else {
      return this._cache.preferredLocale
    }
  }

  canUseBook() {
    if (this._cache && this._cache.canUseBook) {
      return true
    } else {
      return false
    }
  }

  getCache() {
    return this._cache
  }

  isLoggedIn() {
    return _bool(this._cache.accessToken && this._cache.accessToken.length > 0)
  }

  isAnonymous() {
    return _bool(
      this._cache.userId &&
        this._cache.userId.length > 0 &&
        this._cache.userId.indexOf(ANONYMOUS_PREFIX) === 0
    )
  }

  isMinor() {
    return _bool(this.isLoggedIn() && this._cache.isMinor)
  }

  shouldDebitMote() {
    return _bool(this._cache.applyFreeMoteRationing)
  }

  canTrustFreeMotesAllocation() {
    return new Date() < new Date(this._cache.freeMotesMintedEpoch * 1000)
  }

  getRationing() {
    return {
      canMote: this._cache.canMote,
      freeMotesMintedEpoch: this._cache.freeMotesMintedEpoch,
      totalFreeMotes: this._cache.totalFreeMotes,
      availableFreeMotes: this._cache.availableFreeMotes,
      usedFreeMotes: this._cache.usedFreeMotes,
      isUpgradable: this._cache.isUpgradable
    }
  }

  getLegals() {
    var legals = 0
    try {
      legals = !isNaN(this._cache.legals) ? parseInt(this._cache.legals) : 0
    } catch (e) {}
    log('legals is: ' + legals)
    return legals
  }

  isInMarket() {
    if (this.isLoggedIn() && 'productEnum' in this._cache) {
      return true
    } else {
      return false
    }
  }

  getProductName() {
    try {
      if (this.isInMarket()) {
        return this._cache.productName
      }
    } catch (e) {}
    return ''
  }

  getProductEnum() {
    try {
      if (this.isInMarket()) {
        return this._cache.productEnum
      }
    } catch (e) {}
    return ''
  }

  getMaxSecondsPerMote(productEnum) {
    const pe = productEnum || this.getProductEnum()
    if (pe === 'free') {
      return 30
    } else if (pe === 'essential') {
      return 90
    } else if (pe === 'unlimited') {
      return 180
    } else {
      return FALLBACK_MAX_SECONDS_PER_MOTE
    }
  }

  getTranscriptionDisablingReason() {
    const isMinor = this.isMinor()
    try {
      if (this.isInMarket()) {
        if (this._cache.canTranscribe) {
          return undefined
        } else if (!isMinor) {
          return 'upgrade to access'
        }
      }
    } catch (e) {}
    return undefined
  }

  getDescription() {
    try {
      if (this.isInMarket()) {
        const description = this._cache.description
        if (description && Array.isArray(description)) {
          const replacements = this._cache.dateReplacements
          if (replacements && Array.isArray(replacements)) {
            const lines = []
            let line
            for (const originalLine of description) {
              line = originalLine
              for (const pair of replacements) {
                line = line.replace(
                  pair[0],
                  EpochToPrettyDateHoursMinutes(pair[1])
                )
              }
              lines.push(line)
            }
            return lines
          } else {
            return description
          }
        }
      }
    } catch (e) {
      console.error('processing description: ', e)
    }
    return ['']
  }

  isUpgradable() {
    try {
      if (this.isInMarket()) {
        return this._cache.isUpgradable
      }
    } catch (e) {}
    return false
  }

  isPaid() {
    try {
      if (this.isInMarket()) {
        return this._cache.isPaid
      }
    } catch (e) {}
    return false
  }

  isAlreadyInstalled() {
    return Boolean(
      this.isAnonymous() || this.getUserId() || this.getLastUserId()
    )
  }

  ////////////////////// private //////////////////

  _writeAndBroadcast() {
    this._write('broadcast')
  }

  _write(broadcast) {
    this._storageProvider.write(this._cache, broadcast)
  }

  _broadcastToListeners() {
    log('need to send login broadcast with:' + this.isLoggedIn())
    log(`my callbacks are: ${this._callbacks}`)
    for (var callback of this._callbacks) {
      log(`callback is: ${callback}`)
      callback(this.isLoggedIn())
    }
  }

  _missingMinimalPopulation(o) {
    for (var key in MANDATORY_AUTH_PARAMS) {
      if (!(key in o && o[key])) {
        return key
      }
    }
  }

  _resetCache() {
    const _oldCache = Object.assign({}, this._cache)
    this._cache = {}
    for (const key in ALL_AUTH_PARAMS) {
      this._cache[key] = ''
    }
    if (_oldCache.anonymousUserId) {
      this._cache.anonymousUserId = _oldCache.anonymousUserId
      this._cache.anonymousCreatedAt = _oldCache.anonymousCreatedAt
    }

    this._cache.lastUserId = _oldCache.lastUserId ?? ''
    this._cache = _assureNullState(this._cache)
  }

  _connectToStorageProvider() {
    var callback = result => {
      this._cache = Object.assign({}, result)
      log('updated cache to: ', this._cache)
      this._broadcastToListeners()
    }

    this._storageProvider.setCallback(callback)
    this._storageProvider.read()
  }

  ///////////////// test back door /////////////////////
}

class Auth2BaseProvider {
  constructor() {
    this.callback = null
  }

  setCallback(cb) {
    this.callback = cb
  }

  afterMutation(result) {
    if (this.callback) {
      log('We are applying the callback for the mutation', result)
      this.callback(result)
    }
  }
}

class Auth2LocalChromeStorage extends Auth2BaseProvider {
  constructor() {
    super()
    var setOnChanged = () => {
      window.chrome?.storage.onChanged.addListener((object, areaName) => {
        // log("Received a storage area change", object, areaName);
        if (areaName === 'local' && object && Object.keys(object).length) {
          log('Got actionable storage change: ', object)
          this.read()
        }
      })
    }

    window.chrome?.storage?.local?.get(['anonymousUserId'], result => {
      if (result.anonymousUserId) {
        log('yay, we have an anoymousUserId!', result)
        log('we can safely start onChanged listener!', result)
        setOnChanged()
      } else {
        log(
          'before setting onChanged listener, need to initialise anonymous userId and createdAt'
        )
        var o = {
          anonymousUserId: DEFAULT_ANONYMOUS_USER_ID,
          anonymousCreatedAt: DEFAULT_ANONYMOUS_CREATED_AT
        }
        chrome.storage.local.set(o, () => {
          log('Stored anonymous info:')
          setOnChanged()
        })
      }
    })
  }

  write(o, withBroadcast) {
    var oo = _assureNullState(o)
    window.chrome?.storage.local.set(oo, () => {
      log('Stored login information: ', oo)
      if (withBroadcast) {
        this.afterMutation(oo)
      }
    })
  }

  read() {
    window.chrome?.storage?.local?.get(Object.keys(ALL_AUTH_PARAMS), result => {
      const assuredResult = _assureNullState(result)
      log('got back from storage after assuring null state: ', assuredResult)
      // TODO: I don't why we had this but it causes a permanent loop
      this.afterMutation(assuredResult)
    })
  }
}

class Auth2MemoryStorage extends Auth2BaseProvider {
  constructor() {
    super()
    this._innerCache = _assureNullState({})
  }

  write(o, withBroadcast) {
    for (const key in ALL_AUTH_PARAMS) {
      this._innerCache[key] = o[key]
    }
    if (withBroadcast) {
      this.afterMutation(_assureNullState(this._innerCache))
    }
  }

  read() {
    const o = _assureNullState(this._innerCache)
    this.afterMutation(o)
  }
}

class Auth2Error extends Error {}

function _bool(x) {
  return x ? true : false
}

function _assureNullState(candidate) {
  log('Got back result: ', candidate)
  var o = Object.assign({}, candidate)
  if (!o.anonymousUserId) {
    o.anonymousUserId = DEFAULT_ANONYMOUS_USER_ID
    o.anonymousCreatedAt = DEFAULT_ANONYMOUS_CREATED_AT
    log('setting anonymous user id')
  }
  if (!o.userId) {
    o.userId = o.anonymousUserId
    o.createdAt = o.anonymousCreatedAt
    o.accessToken = ''
  }
  return o
}

const singletonInstance = new Auth2()

export default singletonInstance
export { Auth2Error, Auth2LocalChromeStorage, Auth2MemoryStorage }
